import { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, IconButton, CircularProgress, Avatar } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AppLayout from '../../components/AppLayout';
import API_BASE_URL from '../../config/api';
import { toast } from 'react-toastify';

export default function StaffDashboard() {
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current month, 1 = previous month, etc.
  const [performers, setPerformers] = useState({
    bestSales: { name: null, photo: null },
    bestCustomizer: { name: null, photo: null },
    month: '',
    year: ''
  });

  // Helper function to format date without timezone issues
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchBestPerformers();
  }, [monthOffset]);

  const fetchBestPerformers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Calculate the target month (0 = current month, 1 = previous month, etc.)
      const today = new Date();
      const targetDate = new Date(today.getFullYear(), today.getMonth() - monthOffset, 1);
      
      let response;
      if (monthOffset === 0) {
        // Use current month endpoint for current month
        response = await fetch(
          `${API_BASE_URL}/api/dashboard/current-month-performers`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Use date range endpoint for historical months
        const startDate = formatDateLocal(new Date(targetDate.getFullYear(), targetDate.getMonth(), 1));
        const endDate = formatDateLocal(new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0));
        
        response = await fetch(
          `${API_BASE_URL}/api/dashboard/best-performers?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      if (response.ok) {
        const data = await response.json();
        setPerformers({
          bestSales: {
            name: data.bestSalesPerformer?.FullName || 'N/A',
            photo: data.bestSalesPerformer?.Photo || null
          },
          bestCustomizer: {
            name: data.bestCustomizer?.FullName || 'N/A',
            photo: data.bestCustomizer?.Photo || null
          },
          month: monthOffset === 0 ? (data.month || targetDate.toLocaleString('default', { month: 'long' })) : targetDate.toLocaleString('default', { month: 'long' }),
          year: monthOffset === 0 ? (data.year || targetDate.getFullYear()) : targetDate.getFullYear()
        });
      } else {
        toast.error('Failed to fetch best performers');
      }
    } catch (error) {
      console.error('Error fetching best performers:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    setMonthOffset(prev => prev + 1);
  };

  const handleNextMonth = () => {
    if (monthOffset > 0) {
      setMonthOffset(prev => prev - 1);
    }
  };

  const statCards = [
    { title: 'BEST PERFORMER (Sales)', performer: performers.bestSales, bg: '#EDE0C9' },
    { title: 'BEST PERFORMER (Customizer)', performer: performers.bestCustomizer, bg: '#D6EAD8' },
  ];

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: 'calc(100vh - 80px)' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mb: 4,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              letterSpacing: 3,
              color: '#8B7355',
              textDecoration: 'underline',
              fontSize: { xs: '0.9rem', md: '1.5rem' },
              textUnderlineOffset: 12,
              textDecorationThickness: 2,
            }}
          >
            STAFF DASHBOARD
          </Typography>
        </Box>

        {/* Month Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4, gap: 2 }}>
          <IconButton 
            onClick={handlePreviousMonth}
            sx={{ 
              bgcolor: '#C4A574', 
              color: 'white',
              borderRadius: 1,
              '&:hover': { bgcolor: '#B39565' }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              minWidth: 200,
              textAlign: 'center',
              letterSpacing: 1
            }}
          >
            {performers.month} {performers.year}
          </Typography>
          
          <IconButton 
            onClick={handleNextMonth}
            disabled={monthOffset === 0}
            sx={{ 
              bgcolor: monthOffset === 0 ? '#E0E0E0' : '#C4A574', 
              color: 'black',
              borderRadius: 1,
              '&:hover': { bgcolor: monthOffset === 0 ? '#E0E0E0' : '#B39565' },
              '&:disabled': { bgcolor: '#E0E0E0', color: '#999' }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress sx={{ color: '#C4A574' }} />
          </Box>
        ) : (
          <Grid
            container
            spacing={3}
            sx={{
              maxWidth: 1400,
              mx: 'auto',
              justifyContent: 'center',
            }}
          >
            {statCards.map((c, i) => (
              <Grid
                item
                key={i}
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  flexBasis: { xs: '100%', sm: '50%', md: '32%', lg: '25%' },
                  maxWidth: { xs: '100%', sm: '50%', md: '32%', lg: '25%' },
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    p: 3,
                    height: { xs: 140, sm: 160, md: 180 },
                    borderRadius: 3,
                    backgroundColor: c.bg,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  {/* Avatar with Crown on the LEFT */}
                  <Box sx={{ position: 'relative', mr: 2 }}>
                    {/* Crown SVG */}
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 24,
                        height: 24,
                        fill: '#FFD700',
                        zIndex: 2
                      }}
                    >
                      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5m14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
                    </Box>
                    <Avatar
                      src={c.performer.photo ? `${API_BASE_URL}${c.performer.photo}` : undefined}
                      sx={{
                        width: 70,
                        height: 70,
                        border: '3px solid #FFD700',
                        bgcolor: '#FF9800',
                        color: 'black',
                        fontSize: '1.5rem',
                        fontWeight: 700
                      }}
                    >
                      {c.performer.name && c.performer.name !== 'N/A' ? c.performer.name.charAt(0).toUpperCase() : '?'}
                    </Avatar>
                  </Box>
                  
                  {/* Text on the RIGHT */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        letterSpacing: 1,
                      }}
                    >
                      {c.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                      }}
                    >
                      {c.performer.name}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Bottom Gradient */}
        <Box
          sx={{
            mt: 6,
            height: 60,
            background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.2))',
            borderRadius: 2,
          }}
        />
      </Box>
    </AppLayout>
  );
}
